import { Fragment, useMemo } from 'react'
import { Box, Text, Grid, GridItem, VStack } from '@chakra-ui/react';
import { doorsAndPeople, sumDoors } from '../../../lib/turfCounts'
import type {
  ItineraryCanvasserRowDto,
  ItineraryAssignmentDto,
  AssignmentScheduleInfoDto,
} from '../../../types/campaign'
import { ItineraryCellDropZone } from './ItineraryCellDropZone'
import { PersonAvatar } from '../../shared/PersonAvatar'

interface ItineraryWeekGridProps {
  weekStart: string
  canvassers: ItineraryCanvasserRowDto[]
  scheduleMap?: Record<string, AssignmentScheduleInfoDto>
  onRemove?: (assignmentId: string) => void
  onToggleAvailability?: (grantId: string, dateStr: string, isAvailable: boolean) => void
}

function toLocalDateStr(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function getWeekDates(weekStart: string): { dateStr: string; dayLabel: string; dayNum: string }[] {
  const start = new Date(weekStart + 'T00:00:00')
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

  return days.map((day, i) => {
    const d = new Date(start)
    d.setDate(d.getDate() + i)
    const dateStr = toLocalDateStr(d)
    return {
      dateStr,
      dayLabel: day,
      dayNum: `${months[d.getMonth()]} ${d.getDate()}`,
    }
  })
}

function isToday(dateStr: string): boolean {
  return dateStr === toLocalDateStr(new Date())
}

export function ItineraryWeekGrid({
  weekStart,
  canvassers,
  scheduleMap,
  onRemove,
  onToggleAvailability,
}: ItineraryWeekGridProps) {
  const weekDates = getWeekDates(weekStart)

  // Compute daily summary totals
  const dailySummary = useMemo(() => {
    const summary: Record<string, { turfs: number; doors: number | null; people: number; contacts: number }> = {}
    for (const { dateStr } of weekDates) {
      let turfs = 0
      let people = 0
      const turfDoors: (number | null | undefined)[] = []
      let contacts = 0
      for (const canvasser of canvassers) {
        const dayAssignments = canvasser.dayAssignments[dateStr] ?? []
        turfs += dayAssignments.length
        for (const a of dayAssignments) {
          people += a.turfMemberCount
          turfDoors.push(a.turfDoorCount)
          contacts += a.contactsMade
        }
      }
      summary[dateStr] = { turfs, doors: sumDoors(turfDoors), people, contacts }
    }
    return summary
  }, [canvassers, weekDates])

  return (
    <Box overflowX="auto" flex={1}>
      <Grid
        templateColumns="160px repeat(7, 1fr)"
        templateRows="auto"
        gap="2px"
        minW="900px"
      >
        {/* Header row: empty cell + 7 day headers */}
        <GridItem bg="surface" p={2} borderRadius="md">
          <Text fontSize="xs" fontWeight="medium" color="gray.500">
            Canvasser
          </Text>
        </GridItem>
        {weekDates.map(({ dateStr, dayLabel, dayNum }) => (
          <GridItem
            key={dateStr}
            bg={isToday(dateStr) ? 'accent.soft' : 'white'}
            p={2}
            borderRadius="md"
            textAlign="center"
            borderBottom={isToday(dateStr) ? '2px solid' : undefined}
            borderColor={isToday(dateStr) ? 'accent' : undefined}
          >
            <Text fontSize="xs" fontWeight="bold" color={isToday(dateStr) ? 'accent' : 'gray.700'}>
              {dayLabel}
            </Text>
            <Text fontSize="2xs" color={isToday(dateStr) ? 'accent' : 'gray.500'}>
              {dayNum}
            </Text>
          </GridItem>
        ))}

        {/* Canvasser rows */}
        {canvassers.map(canvasser => (
          <Fragment key={canvasser.grantId}>
            {/* Canvasser name cell */}
            <GridItem
              bg="surface"
              p={2}
              borderRadius="md"
              display="flex"
              alignItems="flex-start"
            >
              <VStack gap={1} align="start">
                <Text fontSize="xs" fontWeight="medium" lineClamp={2}>
                  {canvasser.fullName}
                </Text>
                <PersonAvatar name={canvasser.fullName} src={canvasser.portraitUrl} size="xs" />
              </VStack>
            </GridItem>

            {/* 7 day cells */}
            {weekDates.map(({ dateStr }) => {
              const dayAssignments: ItineraryAssignmentDto[] =
                canvasser.dayAssignments[dateStr] ?? []

              // Availability: default to true if not explicitly set
              const isAvailable = canvasser.availability?.[dateStr] !== false

              return (
                <GridItem key={`${canvasser.grantId}-${dateStr}`}>
                  <ItineraryCellDropZone
                    grantId={canvasser.grantId}
                    dateStr={dateStr}
                    assignments={dayAssignments}
                    scheduleMap={scheduleMap}
                    isAvailable={isAvailable}
                    onRemove={onRemove}
                    onToggleAvailability={onToggleAvailability}
                  />
                </GridItem>
              )
            })}
          </Fragment>
        ))}

        {/* Daily summary row */}
        {canvassers.length > 0 && (
          <>
            <GridItem bg="gray.50" p={2} borderRadius="md" borderTop="2px solid" borderColor="gray.200">
              <Text fontSize="2xs" fontWeight="bold" color="gray.500">
                Daily Totals
              </Text>
            </GridItem>
            {weekDates.map(({ dateStr }) => {
              const { turfs, doors, people, contacts } = dailySummary[dateStr] ?? { turfs: 0, doors: null, people: 0, contacts: 0 }
              return (
                <GridItem
                  key={`summary-${dateStr}`}
                  bg="gray.50"
                  p={2}
                  borderRadius="md"
                  borderTop="2px solid"
                  borderColor="gray.200"
                  textAlign="center"
                >
                  <VStack gap={0}>
                    <Text fontSize="2xs" fontWeight="bold" color="gray.600">
                      {turfs} turf{turfs !== 1 ? 's' : ''}
                    </Text>
                    <Text fontSize="2xs" color="gray.500">
                      {doorsAndPeople(doors, people)}
                    </Text>
                    {contacts > 0 && (
                      <Text fontSize="2xs" color="green.600">
                        {contacts} contacted
                      </Text>
                    )}
                  </VStack>
                </GridItem>
              );
            })}
          </>
        )}

        {/* Empty state */}
        {canvassers.length === 0 && (
          <GridItem colSpan={8} py={8} textAlign="center">
            <Text color="gray.400" fontSize="sm">
              No canvassers available. Add users to your committee to get started.
            </Text>
          </GridItem>
        )}
      </Grid>
    </Box>
  );
}
